import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';

import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { CustomDateAdapter } from '../../../../../../shared/services/custom-date-adapter';
import { MaskService } from '../../../../../../shared/services/mask.service';
import { LimitDateValidator } from '../../../../../../shared/validators/limit-date.validator';
import { ConciliacaoBancariaService } from '../../../../../../shared/services/apis/financas/conciliacao-bancaria.service';
import { BuscarParaAssociarRequest } from '../../../../../../shared/services/requests/financas/buscar-para-associar.request';
import { ConciliarRequest } from '../../../../../../shared/services/requests/financas/conciliar.request';
import { ExtratoBancarioItemStruct } from '../../../../../../shared/services/structs/financas/extrato-bancario-item.struct';
import { RegistroAssociavelItemStruct } from '../../../../../../shared/services/structs/financas/registro-associavel-item.struct';
import { TipoMatchConciliacaoEnum } from '../../../../../../shared/enums/financas/tipo-match-conciliacao.enum';
import { NaturezaMovimentacaoEnum } from '../../../../../../shared/enums/financas/natureza-movimentacao.enum';
import { UtilService } from '../../../../../../shared/services/util.service';
import { PeriodoBuscaEnum } from '../../../../../../shared/enums/dfe/periodo-busca.enum';
import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';

@Component({
  selector: 'app-associar-lancamento',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    AbntPipe,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './associar-lancamento.component.html',
  styleUrls: ['./associar-lancamento.component.css'],
})
export class AssociarLancamentoComponent implements OnInit {
  @Input() extrato!: ExtratoBancarioItemStruct;
  @Input() idContaFinanceira!: number;

  @Output() revisarValores = new EventEmitter<RegistroAssociavelItemStruct[]>();
  @Output() conciliado = new EventEmitter<void>();
  @Output() fechar = new EventEmitter<void>();

  isLoading = false;
  isLoadingMais = false;
  isSalvando = false;

  itens: RegistroAssociavelItemStruct[] = [];
  selecionados = new Set<string>();

  _paginaAtual = 0;
  _temMais = false;

  model!: FormGroup;

  periodoBuscaEnum = PeriodoBuscaEnum;
  periodoSelecionado: PeriodoBuscaEnum = PeriodoBuscaEnum.Ultimos30Dias;
  limiteMaxDias = 60;
  private periodoSelecionadoAplicado: PeriodoBuscaEnum = PeriodoBuscaEnum.Ultimos30Dias;
  private periodoIntervaloDeAplicado: Date | null = null;
  private periodoIntervaloAteAplicado: Date | null = null;
  private periodoMenuFoiAplicado = false;

  @ViewChild('periodoMenuTrigger') periodoMenuTrigger?: MatMenuTrigger;

  tiposDisponiveis: { label: string, value: TipoMatchConciliacaoEnum | null }[] = [];

  get totalBanco(): number {
    return UtilService.arredondarAbnt(Math.abs(this.extrato.valor), 2);
  }

  get totalSelecionado(): number {
    let total = 0;
    for (const key of this.selecionados) {
      const item = this.itens.find(i => this._key(i) === key);
      if (item) total += item.valorEmAberto;
    }
    return UtilService.arredondarAbnt(total, 2);
  }

  get faltaConciliar(): number {
    return UtilService.arredondarAbnt((this.totalBanco - this.totalSelecionado), 2);
  }

  get podeConciliar(): boolean {
    return this.selecionados.size > 0 && Math.abs(this.faltaConciliar) < 0.01;
  }

  get periodoLabel(): string {
    switch (this.periodoSelecionado) {
      case PeriodoBuscaEnum.Hoje: return 'hoje';
      case PeriodoBuscaEnum.DaSemana: return 'desta semana';
      case PeriodoBuscaEnum.DoMes: return 'do mês';
      case PeriodoBuscaEnum.Ultimos30Dias: return 'últimos 30 dias';
      case PeriodoBuscaEnum.DoIntervalo: return 'do intervalo';
      default: return 'últimos 30 dias';
    }
  }

  get podeRevisar(): boolean {
    return this.selecionados.size > 0;
  }

  constructor(
    private fb: FormBuilder,
    private conciliacaoBancariaService: ConciliacaoBancariaService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.tiposDisponiveis = [
      { label: 'Todos', value: null },
      { label: 'Movimentação', value: TipoMatchConciliacaoEnum.Movimentacao }
    ];

    if (this.extrato.idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Saida) {
      this.tiposDisponiveis.push({ label: 'Contas a Pagar', value: TipoMatchConciliacaoEnum.ContaPagar });
    } else if (this.extrato.idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Entrada) {
      this.tiposDisponiveis.push({ label: 'Contas a Receber', value: TipoMatchConciliacaoEnum.ContaReceber });
    }

    const hoje = new Date();
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(hoje.getDate() - 30);

    this.model = this.fb.group({
      dataDe: [trintaDiasAtras, Validators.required],
      dataAte: [hoje, Validators.required],
      tipo: [null],
      busca: [''],
    }, { validators: LimitDateValidator.rangeLimit(2) });

    this.salvarPeriodoAplicado();
    this.buscar();
  }

  // ── Período ──────────────────────────────────────────────────────────────────

  selecionarPeriodo(periodo: PeriodoBuscaEnum): void {
    this.periodoSelecionado = periodo;
    if (periodo !== PeriodoBuscaEnum.DoIntervalo)
      this.calcularDatasParaPeriodo(periodo);
  }

  private calcularDatasParaPeriodo(periodo: PeriodoBuscaEnum): void {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    let dataDe: Date;
    let dataAte: Date;

    switch (periodo) {
      case PeriodoBuscaEnum.Hoje:
        dataDe = new Date(hoje);
        dataAte = new Date(hoje);
        break;
      case PeriodoBuscaEnum.DaSemana: {
        dataDe = new Date(hoje);
        dataDe.setDate(hoje.getDate() - hoje.getDay());
        dataAte = new Date(dataDe);
        dataAte.setDate(dataDe.getDate() + 6);
        break;
      }
      case PeriodoBuscaEnum.DoMes: {
        dataDe = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        dataAte = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        break;
      }
      case PeriodoBuscaEnum.Ultimos30Dias:
      default: {
        dataDe = new Date(hoje);
        dataDe.setDate(hoje.getDate() - 30);
        dataAte = new Date(hoje);
        break;
      }
    }

    this.model.patchValue({ dataDe, dataAte });
  }

  aplicarPeriodo(trigger?: MatMenuTrigger): void {
    if (this.periodoSelecionado === PeriodoBuscaEnum.DoIntervalo) {
      this.model.get('dataDe')?.markAsTouched();
      this.model.get('dataAte')?.markAsTouched();

      if (this.model.get('dataAte')?.hasError('dataInvalida')) {
        this.alertService.show('Atenção', 'A data final não pode ser menor que a data inicial.', AlertType.warning);
        return;
      }
      if (this.model.get('dataAte')?.hasError('intervaloExcedido')) {
        this.alertService.show('Atenção', `O intervalo máximo permitido é de ${this.limiteMaxDias} dias.`, AlertType.warning);
        return;
      }
      if (this.model.get('dataDe')?.hasError('required') || this.model.get('dataAte')?.hasError('required')) {
        this.alertService.show('Atenção', 'Preencha as duas datas do intervalo.', AlertType.warning);
        return;
      }

      const de = new Date(this.model.get('dataDe')!.value);
      const ate = new Date(this.model.get('dataAte')!.value);
      const diffDias = Math.ceil((ate.getTime() - de.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDias > this.limiteMaxDias) {
        this.alertService.show('Atenção', `O intervalo máximo permitido é de ${this.limiteMaxDias} dias.`, AlertType.warning);
        return;
      }
    }

    this.periodoMenuFoiAplicado = true;
    this.salvarPeriodoAplicado();
    this.buscar(true);
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  prepararPeriodoIntervalo(): void {
    this.periodoMenuFoiAplicado = false;
  }

  cancelarPeriodoIntervalo(trigger?: MatMenuTrigger): void {
    this.restaurarPeriodoAplicado();
    this.periodoMenuFoiAplicado = true;
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  onPeriodoMenuClosed(): void {
    if (!this.periodoMenuFoiAplicado)
      this.restaurarPeriodoAplicado();
  }

  private salvarPeriodoAplicado(): void {
    this.periodoSelecionadoAplicado = this.periodoSelecionado;
    this.periodoIntervaloDeAplicado = this.cloneDate(this.model?.get('dataDe')?.value ?? null);
    this.periodoIntervaloAteAplicado = this.cloneDate(this.model?.get('dataAte')?.value ?? null);
  }

  private restaurarPeriodoAplicado(): void {
    this.periodoSelecionado = this.periodoSelecionadoAplicado;
    this.model.patchValue({
      dataDe: this.cloneDate(this.periodoIntervaloDeAplicado),
      dataAte: this.cloneDate(this.periodoIntervaloAteAplicado),
    });
  }

  private cloneDate(data: Date | null): Date | null {
    return data ? new Date(data) : null;
  }

  limparSelecao(): void {
    this.selecionados.clear();
  }

  // ── Busca / Paginação ─────────────────────────────────────────────────────────

  buscar(resetar = true): void {
    if (resetar) {
      if (this.isLoading) return;
      this._paginaAtual = 0;
      this.itens = [];
      this.selecionados.clear();
      this._temMais = false;
      this.isLoading = true;
    } else {
      if (this.isLoadingMais) return;
      this.isLoadingMais = true;
    }

    const v = this.model.getRawValue();
    const req: BuscarParaAssociarRequest = {
      idContaFinanceira: this.idContaFinanceira,
      dataInicio: v.dataDe ? this._formatDate(new Date(v.dataDe)) : null,
      dataFim: v.dataAte ? this._formatDate(new Date(v.dataAte)) : null,
      busca: v.busca || null,
      tipo: v.tipo,
      pageIndex: this._paginaAtual,
      idNaturezaMovimentacao: this.extrato.idNaturezaMovimentacao
    };

    this.conciliacaoBancariaService.buscarParaAssociar(req).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.isLoadingMais = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.itens = [...this.itens, ...res.itens];
        this._temMais = res.temMais;
      },
      error: (err) => {
        this.isLoading = false;
        this.isLoadingMais = false;
        this.alertService.show('Erro', err.message, AlertType.error);
      },
    });
  }

  proximaPagina(): void {
    this._paginaAtual++;
    this.buscar(false);
  }

  // ── Seleção ───────────────────────────────────────────────────────────────────

  toggleSelecionado(item: RegistroAssociavelItemStruct): void {
    const key = this._key(item);
    if (this.selecionados.has(key)) {
      this.selecionados.delete(key);
    } else {
      this.selecionados.add(key);
    }
  }

  isSelecionado(item: RegistroAssociavelItemStruct): boolean {
    return this.selecionados.has(this._key(item));
  }

  // ── Confirmar / Ações ─────────────────────────────────────────────────────────

  confirmar(): void {
    if (!this.podeConciliar || this.isSalvando) return;
    this.isSalvando = true;

    const idsMovimentacao: number[] = [];
    const idsContasPagarParcela: number[] = [];
    const idsContasReceberParcela: number[] = [];

    for (const key of this.selecionados) {
      const item = this.itens.find(i => this._key(i) === key);
      if (!item) continue;
      if (item.tipoMatch === TipoMatchConciliacaoEnum.Movimentacao) idsMovimentacao.push(item.idRegistro);
      else if (item.tipoMatch === TipoMatchConciliacaoEnum.ContaPagar) idsContasPagarParcela.push(item.idRegistro);
      else if (item.tipoMatch === TipoMatchConciliacaoEnum.ContaReceber) idsContasReceberParcela.push(item.idRegistro);
    }

    const req: ConciliarRequest = {
      idExtratoBancario: this.extrato.idExtratoBancario,
      idsMovimentacao,
      idsContasPagarParcela,
      idsContasReceberParcela,
    };

    this.conciliacaoBancariaService.conciliar(req).subscribe({
      next: (res) => {
        this.isSalvando = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Conciliação realizada com sucesso.', AlertType.success);
        this.conciliado.emit();
      },
      error: (err) => {
        this.isSalvando = false;
        this.alertService.show('Erro', err.message, AlertType.error);
      },
    });
  }

  onFechar(): void {
    this.fechar.emit();
  }

  onRevisarValores(): void {
    const itensSelecionados = this.itens.filter(i => this.isSelecionado(i));
    this.revisarValores.emit(itensSelecionados);
  }

  tipoLabel(tipoMatch: number): string {
    switch (tipoMatch) {
      case TipoMatchConciliacaoEnum.Movimentacao: return 'Movimentação';
      case TipoMatchConciliacaoEnum.ContaPagar: return 'Conta a Pagar';
      case TipoMatchConciliacaoEnum.ContaReceber: return 'Conta a Receber';
      default: return String(tipoMatch);
    }
  }

  onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  private _key(item: RegistroAssociavelItemStruct): string {
    return `${item.tipoMatch}_${item.idRegistro}`;
  }

  private _formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
}
